import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import FormControlLabel from "@mui/material/FormControlLabel"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { retrieveUserProfile, selectProfile } from "../../users/reducer"
import GreetingAudioPlayer from "./GreetingAudioPlayer"
import {
  nextAreaCodeSelection,
  normalizeSuggestedAreaCode,
} from "./brokerageAreaCode"
import {
  GREETING_MAX_RECORD_SECONDS,
  convertBlobToWavFile,
  pickMediaRecorderMimeType,
  validateGreetingFile,
} from "./greetingAudio"

function firstApiErrorMessage(data, fallback) {
  if (!data || typeof data !== "object") {
    return fallback
  }
  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail
  }
  if (Array.isArray(data.detail) && data.detail.length) {
    return data.detail.map(String).join(" ")
  }
  for (const key of [
    "area_code",
    "phone_number",
    "twilio_voice_phone",
    "greeting_audio",
    "non_field_errors",
  ]) {
    const value = data[key]
    if (typeof value === "string" && value.trim()) {
      return value
    }
    if (Array.isArray(value) && value.length) {
      return value.map(String).join(" ")
    }
  }
  return fallback
}

function formatRecordingSeconds(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, "0")}`
}

/** textingOnly: first-login setup on the texts page, worded for texting. */
export default function ConciergeBuyCallingNumberCard({
  onStatusMessage,
  textingOnly = false,
}) {
  const profile = useSelector(selectProfile)
  const axios = useAuthorizedAxiosClient()
  const dispatch = useDispatch()
  const fileInputRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const mediaStreamRef = useRef(null)
  const recordingChunksRef = useRef([])
  const recordingTimerRef = useRef(null)
  const recordedPreviewUrlRef = useRef(null)
  const lastAccountIdRef = useRef(null)
  const userHasEditedAreaCodeRef = useRef(false)
  const [areaCode, setAreaCode] = useState(() =>
    normalizeSuggestedAreaCode(profile?.suggested_area_code)
  )
  const areaCodeRef = useRef(areaCode)
  areaCodeRef.current = areaCode
  const [areaCodeUserEdited, setAreaCodeUserEdited] = useState(false)
  const [areaCodeError, setAreaCodeError] = useState("")
  const [statusAlert, setStatusAlert] = useState(null)
  const [numbers, setNumbers] = useState([])
  const [selected, setSelected] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [greeting, setGreeting] = useState({
    has_greeting: false,
    reloadKey: null,
    playUrl: null,
  })
  const [isGreetingLoading, setIsGreetingLoading] = useState(false)
  const [isUploadingGreeting, setIsUploadingGreeting] = useState(false)
  const [isClearingGreeting, setIsClearingGreeting] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isProcessingRecording, setIsProcessingRecording] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [recordedPreviewUrl, setRecordedPreviewUrl] = useState(null)
  const [pendingRecordedFile, setPendingRecordedFile] = useState(null)

  function greetingFromResponse(data) {
    return {
      has_greeting: !!data?.has_greeting,
      reloadKey:
        data?.greeting_modified || (data?.has_greeting ? Date.now() : null),
      playUrl: data?.greeting_play_url || null,
    }
  }

  // Profile returns the DID for the currently selected parent account.
  const assigned = profile?.twilio_voice_phone
  const accountId = profile?.active_account?.id
  const suggestedAreaCode = profile?.suggested_area_code
  const accountLabel =
    profile?.account_owner_name ||
    profile?.active_account?.name ||
    "this account"
  const showingBrokerageSuggestion =
    !areaCodeUserEdited &&
    !!areaCode &&
    areaCode === normalizeSuggestedAreaCode(suggestedAreaCode)

  function notify(message, severity = "info") {
    setStatusAlert(message ? { message, severity } : null)
    if (onStatusMessage && message) {
      onStatusMessage(message, severity)
    }
  }

  function clearPendingRecording() {
    if (recordedPreviewUrlRef.current) {
      URL.revokeObjectURL(recordedPreviewUrlRef.current)
      recordedPreviewUrlRef.current = null
    }
    setRecordedPreviewUrl(null)
    setPendingRecordedFile(null)
  }

  function stopMediaStream() {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    mediaStreamRef.current = null
  }

  function clearRecordingTimer() {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
      recordingTimerRef.current = null
    }
  }

  function loadGreeting() {
    if (!assigned) {
      setGreeting({ has_greeting: false, reloadKey: null, playUrl: null })
      return
    }
    setIsGreetingLoading(true)
    axios
      .get("/crm/api/twilio/concierge-calling-numbers/greeting/")
      .then(({ data }) => {
        setGreeting(greetingFromResponse(data))
      })
      .catch(() => {
        setGreeting({ has_greeting: false, reloadKey: null, playUrl: null })
      })
      .finally(() => setIsGreetingLoading(false))
  }

  useEffect(() => {
    loadGreeting()
    // Reload when assigned number / active account changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigned, profile?.active_account?.id, axios])

  useEffect(() => {
    const next = nextAreaCodeSelection({
      accountId,
      lastAccountId: lastAccountIdRef.current,
      suggestedAreaCode,
      currentAreaCode: areaCodeRef.current,
      userHasEdited: userHasEditedAreaCodeRef.current,
    })
    lastAccountIdRef.current = next.lastAccountId
    userHasEditedAreaCodeRef.current = next.userHasEdited
    setAreaCodeUserEdited(next.userHasEdited)
    if (next.areaCode !== areaCodeRef.current) {
      setAreaCode(next.areaCode)
    }
  }, [accountId, suggestedAreaCode])

  useEffect(
    () => () => {
      clearRecordingTimer()
      stopMediaStream()
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop()
      }
      if (recordedPreviewUrlRef.current) {
        URL.revokeObjectURL(recordedPreviewUrlRef.current)
      }
    },
    []
  )

  function uploadGreetingFile(file, successMessage) {
    const validationError = validateGreetingFile(file)
    if (validationError) {
      notify(validationError, "error")
      return Promise.resolve()
    }

    const formData = new FormData()
    formData.append("greeting_audio", file)
    setIsUploadingGreeting(true)
    return axios
      .put("/crm/api/twilio/concierge-calling-numbers/greeting/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        setGreeting(greetingFromResponse(data))
        clearPendingRecording()
        dispatch(retrieveUserProfile())
        notify(successMessage, "success")
      })
      .catch((error) => {
        notify(
          firstApiErrorMessage(
            error?.response?.data,
            "Could not save greeting. Please try again."
          ),
          "error"
        )
      })
      .finally(() => {
        setIsUploadingGreeting(false)
      })
  }

  function handleAreaCodeChange(e) {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 3)
    userHasEditedAreaCodeRef.current = true
    setAreaCodeUserEdited(true)
    setAreaCode(digitsOnly)
    setAreaCodeError("")
  }

  function handleSearch() {
    const trimmed = areaCode.trim()
    if (trimmed && trimmed.length !== 3) {
      const message = "Enter a 3-digit area code (for example 415)."
      setAreaCodeError(message)
      notify(message, "error")
      setNumbers([])
      return
    }

    setIsSearching(true)
    setSelected("")
    setAreaCodeError("")
    setStatusAlert(null)
    const params = {}
    if (trimmed) {
      params.area_code = trimmed
    }
    axios
      .get("/crm/api/twilio/concierge-calling-numbers/available/", { params })
      .then(({ data }) => {
        const found = data.numbers || []
        setNumbers(found)
        if (data.message) {
          notify(data.message, found.length ? "info" : "warning")
        } else if (!found.length) {
          notify(
            trimmed
              ? `No numbers available for area code ${trimmed}. Try another area code.`
              : "No available numbers found right now. Try again or use an area code.",
            "warning"
          )
        }
      })
      .catch((error) => {
        const message = firstApiErrorMessage(
          error?.response?.data,
          "Could not search for phone numbers. Please try again."
        )
        setAreaCodeError(message)
        notify(message, "error")
        setNumbers([])
      })
      .finally(() => setIsSearching(false))
  }

  function handleBuy() {
    if (!selected) {
      return
    }
    setIsPurchasing(true)
    setStatusAlert(null)
    axios
      .post("/crm/api/twilio/concierge-calling-numbers/purchase/", {
        phone_number: selected,
      })
      .then(() => {
        notify(
          `Number purchased for ${accountLabel}. You can call and text candidates on this account now.`,
          "success"
        )
        setNumbers([])
        setSelected("")
        return dispatch(retrieveUserProfile())
      })
      .catch((error) => {
        notify(
          firstApiErrorMessage(
            error?.response?.data,
            "Purchase failed. Please try another number or contact Support."
          ),
          "error"
        )
      })
      .finally(() => setIsPurchasing(false))
  }

  function handleGreetingFileChange(e) {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }
    uploadGreetingFile(
      file,
      "Custom greeting uploaded. Callers will hear your recording."
    ).finally(() => {
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    })
  }

  async function finalizeRecording() {
    const chunks = recordingChunksRef.current
    recordingChunksRef.current = []
    if (!chunks.length) {
      notify("No audio was captured. Please try recording again.", "error")
      return
    }

    setIsProcessingRecording(true)
    try {
      const mimeType = mediaRecorderRef.current?.mimeType || "audio/webm"
      const recordedBlob = new Blob(chunks, { type: mimeType })
      const wavFile = await convertBlobToWavFile(recordedBlob)
      clearPendingRecording()
      const previewUrl = URL.createObjectURL(wavFile)
      recordedPreviewUrlRef.current = previewUrl
      setRecordedPreviewUrl(previewUrl)
      setPendingRecordedFile(wavFile)
    } catch (error) {
      notify(
        error?.message ||
          "Could not process your recording. Try uploading a file instead.",
        "error"
      )
    } finally {
      setIsProcessingRecording(false)
    }
  }

  function stopRecording() {
    clearRecordingTimer()
    setIsRecording(false)
    setRecordingSeconds(0)
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state === "recording") {
      recorder.stop()
    } else {
      stopMediaStream()
    }
  }

  async function handleStartRecording() {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      notify(
        "Recording is not supported in this browser. Upload a file instead.",
        "error"
      )
      return
    }
    if (typeof MediaRecorder === "undefined") {
      notify(
        "Recording is not supported in this browser. Upload a file instead.",
        "error"
      )
      return
    }
    const mimeType = pickMediaRecorderMimeType()
    if (!mimeType) {
      notify(
        "Recording is not supported in this browser. Upload a file instead.",
        "error"
      )
      return
    }

    clearPendingRecording()
    setStatusAlert(null)
    recordingChunksRef.current = []

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream
      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          recordingChunksRef.current.push(event.data)
        }
      }
      recorder.onstop = () => {
        stopMediaStream()
        finalizeRecording()
      }
      recorder.onerror = () => {
        stopRecording()
        notify("Recording failed. Please try again.", "error")
      }

      recorder.start()
      setIsRecording(true)
      setRecordingSeconds(0)
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => {
          const next = prev + 1
          if (next >= GREETING_MAX_RECORD_SECONDS) {
            stopRecording()
          }
          return next
        })
      }, 1000)
    } catch (error) {
      stopMediaStream()
      notify(
        "Microphone access is required to record a greeting. Check browser permissions or upload a file.",
        "error"
      )
    }
  }

  function handleSaveRecordedGreeting() {
    if (!pendingRecordedFile) {
      return
    }
    uploadGreetingFile(
      pendingRecordedFile,
      "Custom greeting saved. Callers will hear your recording."
    )
  }

  function handleClearGreeting() {
    setIsClearingGreeting(true)
    axios
      .delete("/crm/api/twilio/concierge-calling-numbers/greeting/")
      .then(({ data }) => {
        setGreeting(greetingFromResponse(data))
        clearPendingRecording()
        dispatch(retrieveUserProfile())
        notify(
          "Custom greeting removed. Callers will hear the default spoken message.",
          "success"
        )
      })
      .catch((error) => {
        notify(
          firstApiErrorMessage(
            error?.response?.data,
            "Could not remove greeting. Please try again."
          ),
          "error"
        )
      })
      .finally(() => setIsClearingGreeting(false))
  }

  const greetingBusy =
    isUploadingGreeting || isProcessingRecording || isClearingGreeting

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardHeader
        title={
          textingOnly
            ? "Pick your texting number"
            : "Concierge calling & SMS number"
        }
        titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
        subheader={
          textingOnly
            ? "Search an area code and buy a number. Your texts send from it and replies come back to your inbox."
            : `Buy a Twilio number for ${accountLabel}. The same number is used for calls and SMS. Each parent account needs its own number.`
        }
      />
      <CardContent>
        <Column gap={2}>
          {assigned ? (
            <>
              <Alert severity="success">
                Calling & SMS number for <strong>{accountLabel}</strong>:{" "}
                <strong>{assigned}</strong>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Candidates on this account will see this number for calls and
                  SMS. Switch parent accounts to buy or view numbers for other
                  clients. Conversations are saved on the parent account.
                </Typography>
              </Alert>

              <Column gap={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Inbound voicemail greeting
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Record in your browser or upload MP3/WAV (max 5 MB). If none
                  is set, callers hear the default spoken greeting for{" "}
                  {accountLabel}.
                </Typography>
                {isGreetingLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading greeting…
                  </Typography>
                ) : greeting.has_greeting ? (
                  <Alert
                    severity="info"
                    sx={{ "& .MuiAlert-message": { width: "100%" } }}
                  >
                    Custom greeting is active for this account.
                    <GreetingAudioPlayer
                      reloadKey={greeting.reloadKey}
                      fallbackPlayUrl={greeting.playUrl}
                    />
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    No custom greeting yet — you must add one before placing
                    calls from this account.
                  </Alert>
                )}

                {isRecording && (
                  <Alert severity="error">
                    Recording… {formatRecordingSeconds(recordingSeconds)} (max{" "}
                    {GREETING_MAX_RECORD_SECONDS}s)
                  </Alert>
                )}

                {recordedPreviewUrl && !isRecording && (
                  <Alert
                    severity="info"
                    sx={{ "& .MuiAlert-message": { width: "100%" } }}
                  >
                    Preview your recording before saving.
                    <GreetingAudioPlayer blobUrl={recordedPreviewUrl} />
                  </Alert>
                )}

                <Row gap={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                  {isRecording ? (
                    <LoadingButton
                      variant="contained"
                      color="error"
                      loading={isProcessingRecording}
                      onClick={stopRecording}
                    >
                      Stop recording
                    </LoadingButton>
                  ) : pendingRecordedFile ? (
                    <>
                      <LoadingButton
                        variant="contained"
                        loading={isUploadingGreeting}
                        disabled={greetingBusy}
                        onClick={handleSaveRecordedGreeting}
                      >
                        Save recording
                      </LoadingButton>
                      <Button
                        variant="outlined"
                        color="inherit"
                        disabled={greetingBusy}
                        onClick={clearPendingRecording}
                      >
                        Discard
                      </Button>
                    </>
                  ) : (
                    <>
                      <LoadingButton
                        variant="outlined"
                        loading={isProcessingRecording}
                        disabled={greetingBusy}
                        onClick={handleStartRecording}
                      >
                        Record greeting
                      </LoadingButton>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".mp3,.wav,audio/mpeg,audio/wav"
                        hidden
                        onChange={handleGreetingFileChange}
                      />
                      <LoadingButton
                        variant="contained"
                        loading={isUploadingGreeting}
                        disabled={greetingBusy}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {greeting.has_greeting
                          ? "Replace with upload"
                          : "Upload greeting"}
                      </LoadingButton>
                    </>
                  )}
                  {greeting.has_greeting && !isRecording && (
                    <LoadingButton
                      variant="outlined"
                      color="inherit"
                      loading={isClearingGreeting}
                      disabled={greetingBusy}
                      onClick={handleClearGreeting}
                    >
                      Remove greeting
                    </LoadingButton>
                  )}
                </Row>
              </Column>
            </>
          ) : (
            <>
              <Alert severity="info">
                No calling number for <strong>{accountLabel}</strong> yet.
                Search available Twilio numbers and buy one below. You need a
                separate number for each parent account you support.
              </Alert>
              {statusAlert && (
                <Alert
                  severity={statusAlert.severity}
                  onClose={() => setStatusAlert(null)}
                >
                  {statusAlert.message}
                </Alert>
              )}
              <Row gap={1} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
                <TextField
                  label="Area code (optional)"
                  size="small"
                  value={areaCode}
                  onChange={handleAreaCodeChange}
                  placeholder="415"
                  error={!!areaCodeError}
                  helperText={
                    areaCodeError ||
                    (showingBrokerageSuggestion
                      ? "Suggested from this brokerage. You can change it."
                      : "3 digits. Leave blank to search any number.")
                  }
                  inputProps={{
                    maxLength: 3,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{ width: 220 }}
                />
                <LoadingButton
                  variant="outlined"
                  loading={isSearching}
                  onClick={handleSearch}
                >
                  Search numbers
                </LoadingButton>
              </Row>
              {numbers.length > 0 && (
                <Column gap={1}>
                  <Typography variant="subtitle2">
                    Select a number to buy for {accountLabel}
                  </Typography>
                  <RadioGroup
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                  >
                    {numbers.map((n) => (
                      <FormControlLabel
                        key={n.phone_number}
                        value={n.phone_number}
                        control={<Radio size="small" />}
                        label={`${n.friendly_name || n.phone_number}${
                          n.locality || n.region
                            ? ` — ${[n.locality, n.region]
                                .filter(Boolean)
                                .join(", ")}`
                            : ""
                        }`}
                      />
                    ))}
                  </RadioGroup>
                  <Button
                    variant="contained"
                    disabled={!selected || isPurchasing}
                    onClick={handleBuy}
                  >
                    {isPurchasing ? "Purchasing…" : "Buy & assign number"}
                  </Button>
                </Column>
              )}
            </>
          )}
          {assigned && statusAlert && (
            <Alert
              severity={statusAlert.severity}
              onClose={() => setStatusAlert(null)}
            >
              {statusAlert.message}
            </Alert>
          )}
        </Column>
      </CardContent>
    </Card>
  )
}
